import type { JSX } from 'react';

import { ChevronUp, PlusSquare, Share, Smartphone, X } from 'lucide-react';
import { Drawer } from 'vaul';

import { Button } from '../Button/Button';

import styles from './InstallPrompt.module.css';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * Mobile "add Revvly to your home screen" hint. Auto-opens on mobile web (once
 * per 24h, until installed) and can be opened on demand via `openInstallHint()`.
 *
 * Chromium (Android) gets a one-tap install button; iOS Safari — which has no
 * install API — gets animated Share → Add to Home Screen instructions. Mounted
 * once at the app root (see main.tsx).
 */
export default function InstallPrompt(): JSX.Element | null {
  const { open, setOpen, platform, canPromptInstall, promptInstall, installed } =
    useInstallPrompt();

  // Nothing to offer once installed.
  if (installed) {
    return null;
  }

  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      <Drawer.Portal>
        <Drawer.Overlay className={styles.Overlay} />
        <Drawer.Content className={styles.Content} aria-describedby={undefined}>
          <div className={styles.Handle} aria-hidden="true" />

          <header className={styles.Header}>
            <div className={styles.HeaderTitle}>
              <Smartphone className={styles.HeaderIcon} aria-hidden="true" />
              <Drawer.Title className={styles.Title}>Install Revvly</Drawer.Title>
            </div>
            <Drawer.Close className={styles.CloseButton} aria-label="Dismiss">
              <X aria-hidden="true" />
            </Drawer.Close>
          </header>

          <div className={styles.Body}>
            <p className={styles.Lead}>
              Add Revvly to your home screen for a full-screen app and to receive push
              notifications when it&apos;s closed.
            </p>

            {canPromptInstall ? (
              // Android / Chromium — one-tap native install.
              <Button variant="primary" size="lg" onClick={() => void promptInstall()}>
                Add to Home Screen
              </Button>
            ) : platform === 'ios' ? (
              // iOS Safari — no install API, so show the manual Share steps with
              // an animated cue.
              <>
                <ol className={styles.Steps}>
                  <li className={styles.Step}>
                    <span className={styles.StepNumber}>1</span>
                    <span className={styles.StepText}>
                      Tap the <Share className={styles.InlineIcon} aria-hidden="true" />
                      <strong>Share</strong> button in the browser bar
                    </span>
                  </li>
                  <li className={styles.Step}>
                    <span className={styles.StepNumber}>2</span>
                    <span className={styles.StepText}>
                      Choose{' '}
                      <PlusSquare className={styles.InlineIcon} aria-hidden="true" />
                      <strong>Add to Home Screen</strong>
                    </span>
                  </li>
                </ol>

                {/* Animated cue: a share glyph with a bouncing arrow pointing to
                    where the Share button sits (bottom bar on iPhone Safari). */}
                <div className={styles.Cue} aria-hidden="true">
                  <Share className={styles.CueIcon} />
                  <ChevronUp className={styles.CueArrow} />
                </div>

                <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
                  Got it
                </Button>
              </>
            ) : (
              // Android/other before the native prompt is ready, or any browser
              // without one — the Share-based steps don't apply, so point at the
              // browser menu instead.
              <>
                <p className={styles.Lead}>
                  Open your browser menu and choose <strong>Install app</strong> or{' '}
                  <strong>Add to Home Screen</strong>.
                </p>
                <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
                  Got it
                </Button>
              </>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
